import { formatHoursMinutes, truncateText } from "@repo/legacy/components/helper/Index";
import { t } from "i18next";
import { useMemo } from "react";

import {
	type ChartData,
	type PositionSummary,
	type ProjectProgressTotals,
	STATUS_CONFIG,
	STATUS_SEPARATOR,
} from "../components/ProjectProgressPieBox/ProjectProgressPieBox.types";

import type { ProjectTimeSummary } from "@repo/server-api";

// Helper Functions

const hexToRgba = (hex: string, alpha: number): string => {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const generateUniqueId = (projectName: string, positionName: string, index: number): string =>
	`${projectName}::${positionName}::${index}`;

const generateOpacityMap = (summaries: PositionSummary[]): Record<string, number> => {
	const map: Record<string, number> = {};
	const uniquePositions = Array.from(new Set(summaries.map((s) => s.positionName)));

	// Einfacher linearer Algorithmus: Von 0.4 bis 1.0 verteilen
	const minOpacity = 0.4;
	const maxOpacity = 1.0;
	const step =
		uniquePositions.length > 1 ? (maxOpacity - minOpacity) / (uniquePositions.length - 1) : 0;

	uniquePositions.forEach((position, index) => {
		map[position] = minOpacity + step * index;
	});

	return map;
};

const calculateStatusData = (
	summaries: PositionSummary[],
	selectedIds: string[],
	totalMinutes: number,
): ChartData[] => {
	const filteredSummaries = summaries.filter((s) => selectedIds.includes(s.id));

	return STATUS_CONFIG.map((status) => {
		const value =
			status.id === "LEFT"
				? filteredSummaries.reduce((sum, s) => sum + s.leftMinutes, 0)
				: filteredSummaries.reduce((sum, s) => sum + s.statusMinutes[status.key], 0);

		return {
			id: status.id,
			label: t(status.labelKey),
			value,
			percentage: totalMinutes > 0 ? (value * 100) / totalMinutes : 0,
			color: status.color,
		};
	}).sort((a, b) => a.id.localeCompare(b.id));
};

const calculatePositionStatusData = (
	summaries: PositionSummary[],
	selectedIds: string[],
	opacityMap: Record<string, number>,
): ChartData[] => {
	const filteredSummaries = summaries.filter((s) => selectedIds.includes(s.id));

	// Calculate total for percentage
	const totalMinutes = filteredSummaries.reduce((sum, s) => sum + s.totalMinutes, 0);

	const data: ChartData[] = [];

	// For each summary, create entries for each status
	for (const summary of filteredSummaries) {
		for (const status of STATUS_CONFIG) {
			const count = status.id === "LEFT" ? summary.leftMinutes : summary.statusMinutes[status.key];

			const baseColor = status.color;
			const opacity = opacityMap[summary.positionName] || 1;

			data.push({
				id: `${summary.id}${STATUS_SEPARATOR}${status.id}`,
				label: summary.positionName,
				labelValue: truncateText(summary.positionName, 20), // Optional truncated label
				value: count,
				percentage: totalMinutes > 0 ? (count * 100) / totalMinutes : 0,
				color: hexToRgba(baseColor, opacity),
			});
		}
	}

	// Sort by status first, then by position
	return data.sort((a, b) => {
		const parts_a = a.id.split(STATUS_SEPARATOR);
		const parts_b = b.id.split(STATUS_SEPARATOR);
		const statusA = parts_a[1] || "";
		const statusB = parts_b[1] || "";

		if (statusA !== statusB) {
			return statusA.localeCompare(statusB);
		}

		return a.id.localeCompare(b.id);
	});
};

// Main Hook

export type UseProjectProgressDataResult = {
	positionSummaries: PositionSummary[];
	statusData: ChartData[];
	positionStatusData: ChartData[];
	totals: ProjectProgressTotals;
};

export const useProjectProgressData = (
	projectTimeSummaries: ProjectTimeSummary[],
	selectedPositionIds: string[],
): UseProjectProgressDataResult => {
	// 1. Normalize Position Summaries with unique IDs
	const positionSummaries = useMemo<PositionSummary[]>(() => {
		if (!projectTimeSummaries) return [];

		return projectTimeSummaries.map((item, index) => ({
			id: generateUniqueId(item.projectName, item.positionName, index),
			projectName: item.projectName,
			positionName: item.positionName,
			totalMinutes: item.totalMinutes,
			totalWorkedMinutes: item.totalWorkedMinutes,
			leftMinutes: item.totalMinutes - item.totalWorkedMinutes,
			statusMinutes: {
				approved: item.approvedMinutes,
				pending: item.pendingMinutes,
				open: item.openMinutes,
			},
		}));
	}, [projectTimeSummaries]);

	// 2. Calculate Opacity Map
	const opacityMap = useMemo<Record<string, number>>(() => {
		return generateOpacityMap(positionSummaries);
	}, [positionSummaries]);

	// 3. Calculate Totals
	const totals = useMemo<ProjectProgressTotals>(() => {
		const filteredSummaries = positionSummaries.filter((s) => selectedPositionIds.includes(s.id));

		const totalMinutes = filteredSummaries.reduce((sum, s) => sum + s.totalMinutes, 0);
		const totalWorkedMinutes = filteredSummaries.reduce((sum, s) => sum + s.totalWorkedMinutes, 0);
		const totalLeft = filteredSummaries.reduce((sum, s) => sum + s.leftMinutes, 0);

		return {
			totalMinutes,
			totalWorkedMinutes,
			totalLeft,
			formatted: {
				total: formatHoursMinutes(totalMinutes),
				totalWorked: formatHoursMinutes(totalWorkedMinutes),
			},
		};
	}, [positionSummaries, selectedPositionIds]);

	// 4. Calculate Status Data (inner ring)
	const statusData = useMemo<ChartData[]>(() => {
		return calculateStatusData(positionSummaries, selectedPositionIds, totals.totalMinutes);
	}, [positionSummaries, selectedPositionIds, totals.totalMinutes]);

	// 5. Calculate Position Status Data (outer ring)
	const positionStatusData = useMemo<ChartData[]>(() => {
		return calculatePositionStatusData(positionSummaries, selectedPositionIds, opacityMap);
	}, [positionSummaries, selectedPositionIds, opacityMap]);

	return {
		positionSummaries,
		statusData,
		positionStatusData,
		totals,
	};
};
